// Listing data lookup for email templates (read-only; never writes).
// Searches Resi's own data the agent can see: their seller/listing transactions, their Open Houses,
// and the pipeline sellers they own. Each source is a provider so an MLS/IDX feed can be added later.
import { ref, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { getAccessible } from '../services/recordAccess';
import { getTransactions } from '../services/transactionService';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const money = (v) => { const n = Number(String(v || '').replace(/[^0-9.]/g, '')); return n ? '$' + Math.round(n).toLocaleString('en-US') : ''; };
const list = (val) => Object.entries(val || {}).map(([id, v]) => ({ id, ...v }));
const isImage = (u) => /^https?:\/\/.+\.(jpe?g|png|webp|gif)(\?.*)?$/i.test(String(u || ''));
// Street, City, ST ZIP when the parts exist; otherwise the stored address.
export const fullAddr = (r, street) => { const city = r.city || r.propertyCity; const st = r.state || r.propertyState; const zip = r.zip || r.zipCode || r.propertyZip || r.postalCode; const base = street || r.propertyAddress || r.address || ''; if (!city || /,/.test(base)) return base; return [base, city, [st, zip].filter(Boolean).join(' ')].filter(Boolean).join(', '); };
const firstPhoto = (r) => [r.heroPhotoUrl, r.photoUrl, r.coverPhotoUrl, ...(Array.isArray(r.photos) ? r.photos : Object.values(r.photos || {})), r.photosLink]
  .map((p) => (typeof p === 'string' ? p : p?.url)).find(isImage) || '';
const fmtTime = (t) => { if (!t) return ''; const [h, m] = String(t).split(':').map(Number); if (Number.isNaN(h)) return t; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };
const fmtDate = (d) => { if (!d) return ''; const x = new Date(`${d}T12:00:00`); return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }); };

// Provider interface: { id, label, enabled, search(query, { uid, isAdmin }) => Promise<Result[]> }
// Result: { key, source, address, values: { address, price, beds, baths, sqft, photo_url, listing_url, date, time } }
const transactionsProvider = {
  id: 'transactions', label: 'My listings', enabled: true,
  async search(q, { uid }) {
    const txs = await getTransactions(uid);
    return txs.filter((t) => t.propertyAddress && norm(t.propertyAddress).includes(q) && /sell/i.test(t.type || t.transactionType || 'Seller'))
      .map((t) => ({ key: `tx-${t.id}`, source: 'Listing', address: t.propertyAddress, txId: t.id, values: {
        address: t.propertyAddress, full_address: fullAddr(t, t.propertyAddress), price: money(t.listingPrice || t.listPrice || t.price), beds: t.beds || t.bedrooms || '', baths: t.baths || t.bathrooms || '',
        sqft: t.sqft || t.squareFeet || '', photo_url: firstPhoto(t), listing_url: t.listingUrl || t.mlsUrl || '' } }));
  },
};
const openHousesProvider = {
  id: 'openHouses', label: 'My open houses', enabled: true,
  async search(q, { uid }) {
    const s = await get(ref(database, 'openHouses'));
    const today = new Date().toISOString().slice(0, 10);
    return list(s.val()).filter((o) => o.agentUid === uid && o.address && norm(o.address).includes(q) && (!o.date || o.date >= today))
      .map((o) => ({ key: `oh-${o.id}`, source: `Open house ${o.date || ''}`.trim(), address: o.address, txId: o.listingId || null, values: {
        address: o.address, full_address: fullAddr(o, o.address), date: fmtDate(o.date), time: [fmtTime(o.startTime), fmtTime(o.endTime)].filter(Boolean).join(' - ') } }));
  },
};
const pipelineProvider = {
  id: 'pipeline', label: 'My pipeline sellers', enabled: true,
  async search(q, { uid, isAdmin }) {
    const rows = await getAccessible('sellerInterests', uid, isAdmin); // own + shared (admins all)
    return rows.filter((r) => r.propertyAddress && norm(r.propertyAddress).includes(q) && r.status !== 'Converted')
      .map((r) => ({ key: `si-${r.id}`, source: 'Pipeline seller', address: r.propertyAddress, values: {
        address: r.propertyAddress, full_address: fullAddr(r, r.propertyAddress), beds: r.beds || '', baths: r.baths || '', sqft: r.sqft || '',
        price: r.priceMin && r.priceMax ? `${money(r.priceMin)} - ${money(r.priceMax)}` : money(r.priceMin || r.priceMax || r.estimatedPrice) } }));
  },
};
// TODO(IDX): SimplyRETS / MLS feed is not set up yet. When it is, implement search() against the
// feed (address autocomplete; map beds, baths, sqft, list price, photos[0], listing URL) and flip
// enabled to true. Keep it read-only; never write feed data into Firebase from here.
export const idxProvider = { id: 'idx', label: 'MLS (IDX)', enabled: false, async search() { return []; } };

export const LISTING_PROVIDERS = [transactionsProvider, openHousesProvider, pipelineProvider, idxProvider];

// Address autocomplete across the enabled providers. A failing source (e.g. no read access) is skipped.
export async function searchListings(query, ctx) {
  const q = norm(query);
  if (q.length < 3 || !ctx?.uid) return [];
  const all = await Promise.all(LISTING_PROVIDERS.filter((p) => p.enabled).map((p) => p.search(q, ctx).catch(() => [])));
  const results = all.flat();
  // Merge rows for the same address: earlier providers win, later ones only fill gaps (e.g. open house date/time).
  const byAddr = new Map();
  results.forEach((r) => {
    const k = norm(r.address);
    const prev = byAddr.get(k);
    if (!prev) { byAddr.set(k, { ...r, values: { ...r.values } }); return; }
    Object.entries(r.values).forEach(([f, v]) => { if (v && !prev.values[f]) prev.values[f] = v; });
    prev.source = `${prev.source}, ${r.source}`;
  });
  return [...byAddr.values()].slice(0, 10);
}

// Replace {{token}} with found values in the subject and every block field. Missing values keep the token.
export function fillListingTokens(tpl, values) {
  values = { ...values, full_address: values.full_address || values.address };
  const fill = (s) => (typeof s === 'string' ? s.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, k) => (values[k] ? String(values[k]) : m)) : s);
  return { ...tpl, subject: fill(tpl.subject), blocks: (tpl.blocks || []).map((b) => Object.fromEntries(Object.entries(b).map(([k, v]) => [k, k === 'type' ? v : fill(v)]))) };
}
